import { groupImmunizationRecords, isVaccineScheduleGroup } from '@liza/fhir-utils';
import type { WithId } from '@medplum/core';
import type { Immunization } from '@medplum/fhirtypes';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ImmunizationGroupRow, type ImmunizationGroupRowProps } from './ImmunizationGroupRow';

export type ImmunizationsListProps = Omit<ImmunizationGroupRowProps, 'group'> & {
  /** The patient's whole immunization history. Grouping a partial list gives wrong dose counts. */
  immunizations: WithId<Immunization>[];
};

/**
 * One row per vaccine rather than one per resource. Each row names the vaccine group, its dose count
 * and most recent date, and expands to the dated doses; tuberculin tests, immune globulins and
 * disease history sit under a separate heading because they are not doses. Grouping and ordering
 * come from `groupImmunizationRecords`.
 */
export const ImmunizationsList = ({ immunizations, ...rowProps }: ImmunizationsListProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const { groups } = useMemo(() => groupImmunizationRecords(immunizations), [immunizations]);
  const vaccines = groups.filter(isVaccineScheduleGroup);
  const other = groups.filter((group) => !isVaccineScheduleGroup(group));

  return (
    <div className="mx-auto w-full max-w-4xl">
      <div className="space-y-1">
        {groups.length > 0 ? (
          <>
            {vaccines.map((group) => (
              <ImmunizationGroupRow key={group.key} group={group} {...rowProps} />
            ))}
            {other.length > 0 && (
              <>
                <h3 className="px-3 pt-4 pb-1 text-xs font-medium tracking-wider text-muted-foreground uppercase">
                  {t('otherImmunityRecords')}
                </h3>
                {other.map((group) => (
                  <ImmunizationGroupRow key={group.key} group={group} {...rowProps} />
                ))}
              </>
            )}
          </>
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('noImmunizationsFound')}</p>
        )}
      </div>
    </div>
  );
};
